import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The active studio mode — Marketing | Designer | CGI.
 *
 * - On login we hydrate from `/api/mode` (server-persisted per-user).
 * - `setMode(m)` flips the mode locally AND persists to the server.
 * - `openPicker()` / `closePicker()` controls the fullscreen mode-switcher
 *   that the logo click opens.
 */
const ModeContext = createContext(null);

export const useMode = () => {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error("useMode must be inside a ModeProvider");
  return ctx;
};

export const MODES = {
  designer: {
    id: "designer",
    label: "Designing",
    tagline: "Pure creative play — images, articles, videos",
    accent: "#8B5CF6",
    accentClass: "text-violet-500",
    bgClass: "bg-violet-500",
    home: "/dashboard/designer",
  },
  cgi: {
    id: "cgi",
    label: "CGI",
    tagline: "Movie-grade effects on your generations",
    accent: "#3B82F6",
    accentClass: "text-blue-500",
    bgClass: "bg-blue-500",
    home: "/dashboard/cgi",
    maintenance: true, // locked for non-admins: "temporarily unavailable for fixes"
  },
  threed: {
    id: "threed",
    label: "3D",
    tagline: "Scan, design & print real objects",
    accent: "#10B981",
    accentClass: "text-emerald-500",
    bgClass: "bg-emerald-500",
    home: "/dashboard",
    comingSoon: true,
  },
};

export const ModeProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [mode, setModeState] = useState("designer");
  const [picker, setPicker] = useState({ open: false, loading: false });

  // Hydrate on login — anyone still on the retired Marketing realm lands in Designing.
  useEffect(() => {
    if (!user || !token) return;
    const stored = user.active_mode && MODES[user.active_mode] ? user.active_mode : "designer";
    const allowed = stored === "cgi" && !user.is_admin ? "designer" : stored;
    setModeState(MODES[allowed]?.comingSoon ? "designer" : allowed);
  }, [user?.id, user?.active_mode, token]);

  const setMode = useCallback(
    async (next) => {
      if (!MODES[next] || next === mode) return;
      if (MODES[next].comingSoon) return;
      if (MODES[next].maintenance && !user?.is_admin) return;
      setPicker({ open: true, loading: true });
      try {
        await axios.patch(
          `${API}/mode`,
          { mode: next },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        // Silent — still flip the local mode so the UX never stalls
      }
      // Hold the orbit animation briefly so the user *sees* the transition
      await new Promise((r) => setTimeout(r, 1100));
      setModeState(next);
      setPicker({ open: false, loading: false });
      // Navigate to the new mode's home — let the consumer decide via window
      const home = MODES[next].home;
      if (window.location.pathname !== home) {
        window.history.pushState({}, "", home);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    },
    [mode, token, user?.is_admin]
  );

  const openPicker = useCallback(() => setPicker({ open: true, loading: false }), []);
  const closePicker = useCallback(() => setPicker({ open: false, loading: false }), []);

  const value = {
    mode,
    config: MODES[mode],
    modes: MODES,
    setMode,
    picker,
    openPicker,
    closePicker,
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};

export default ModeContext;
